import {
  TASK_EVENT_TYPES,
  TASK_EVENT_TYPE_LABELS,
  TASK_STATUS_LABELS,
  type TaskEventType,
} from '@netcon-work/shared'
import { useQuery } from '@tanstack/react-query'
import { useEffect, useRef, useState, type ReactNode } from 'react'
import { cn } from '../lib/cn'
import { imNachlauf, useGelesenSetzen, useNachlaufTakt, type FeedEintrag } from '../lib/gelesen'
import { useMerker, useMerkerMenge } from '../lib/merker'
import { aktivitaetsfeedQuery } from '../lib/queries'
import { AufgabenVerweis, useAufgabeOffen } from './AufgabeKontext'
import { Card, CardPad, SourceBadge } from './ui/primitives'
import { hintergrundSchliesst } from './ui/hintergrund'

/**
 * Wie viele **Aufgaben** die Vorschau zeigt – alles Weitere steht im Popup.
 *
 * Vorher waren es zwölf *Zeilen*. Seit der Feed nach Aufgabe gruppiert (siehe `gruppieren`),
 * ist eine Aufgabe eine Zeile; die Zahl bleibt, ihre Einheit ändert sich.
 */
const VORSCHAU_AUFGABEN = 12

/** Die Zeiträume, die das Popup anbietet (Nils, 18.08.2026). */
const ZEITRAEUME = ['14', '30'] as const
type Zeitraum = (typeof ZEITRAEUME)[number]

/**
 * **Beim ersten Mal nur Kommentare** (Nils, 18.08.2026: *„standardmäßig nur Kommentare
 * bitte"*).
 *
 * Der Feed ist als Antwort auf *„ich schreibe einen Kommentar und will die Antwort sehen"*
 * entstanden – Statuswechsel und Label sind der Rand davon, nicht die Mitte. Als
 * **Voreinstellung** und nicht als Zuschnitt der Abfrage: geladen wird weiterhin alles,
 * sonst stünden im Menü keine Anzahlen für das, was gerade nicht gezeigt wird, und ein
 * ausgeschalteter Filter hätte nichts zu zeigen.
 *
 * Wer sie einmal wegklickt, bekommt sie nicht wieder – siehe `useMerkerMenge`.
 */
const FILTER_STANDARD: readonly TaskEventType[] = ['comment']

/** Der Merker für „Gelesene ausblenden" – wie die Filterwahl je Gerät, siehe `lib/merker.ts`. */
const AUSBLENDEN_WERTE = ['an', 'aus'] as const

/**
 * **Was auf meinen Aufgaben passiert ist** (Nils, 18.08.2026).
 *
 * *„Wenn ich beispielsweise hier einen Kommentar schreibe – dann möchte ich ja sehen, wenn
 * jemand da antwortet. Kann der auch in «Mein Bereich» mit rein? Oder wo schlägst du vor?"*
 *
 * ## Warum an zwei Stellen
 *
 * In **Mein Tag**, weil dort der Arbeitstag anfängt und die Frage lautet *„was ist passiert,
 * seit ich zuletzt hingesehen habe"*. Und in **Mein Bereich**, weil ein Mit-Owner „Mein Tag"
 * gar nicht hat ([E-109](../../../../docs/ENTSCHEIDUNGEN.md)) – für ihn ist das seine
 * einzige Ansicht.
 *
 * Dieselbe Komponente, nicht zwei – wie bei den Anfragen. Zwei Dateien wären zwei Stellen,
 * an denen dieselbe Zeile anders aussieht.
 *
 * ## Was drinsteht und was nicht
 *
 * Nur **fremde** Taten auf Aufgaben, mit denen man zu tun hat: bearbeitet, nimmt ab, kommt
 * in Frage, hat sich darin geäußert – oder **wurde darin erwähnt** (18.08.2026,
 * [E-125](../../../../docs/ENTSCHEIDUNGEN.md)). Wer einen Kommentar schreibt, wartet auf eine
 * Antwort; wer mit `@` angesprochen wird, gehört ab da dazu, auch wenn ihn die Aufgabe sonst
 * nichts angeht. Diese Zeilen heißen „hat dich erwähnt" und sind hervorgehoben – ein
 * Kommentar ist eine Mitteilung, eine Erwähnung eine Frage.
 *
 * Die eigenen Taten fehlen bewusst: ein Feed, in dem die Hälfte „du hast …" lautet, ist ein
 * Protokoll und keine Nachricht.
 *
 * ## Vorschau, Filter, Popup (18.08.2026)
 *
 * Nils: *„kannst du da noch einen Filterbutton einbauen mit einem Contextmenü … Die Vorschau
 * da am besten immer mit 10-12 Zeilen, und wenn man auf «Neu für Dich» klickt dann im PopUp
 * 14 Tage, 30 Tage mit anbieten."*
 *
 * Die Vorschau ist damit eine **feste Größe** – zwölf Zeilen, gleich in „Mein Tag" und „Mein
 * Bereich". Sie war vorher acht und daneben eine ungenutzte Variante mit dreißig; eine
 * Vorschau, deren Länge von der Ansicht abhängt, ist zweimal dieselbe Liste mit zwei
 * Antworten auf die Frage „ist das alles?".
 *
 * **Der Filter wirkt auf beide** und wird gemerkt (`localStorage`), wie der im Tages-Popup.
 * Er läuft **im Client** über die geladene Menge: ein Haken ist damit sofort wirksam, ohne
 * Rundlauf (Leitplanke 1 und 2).
 *
 * ## Eine Zeile je Aufgabe (20.08.2026, [E-198](../../../../docs/ENTSCHEIDUNGEN.md))
 *
 * Nils: *„bei gleichen Aufgaben ggf. den neusten «+ 3 weitere» anzeigen"*. Vier Kommentare an
 * einem Ticket füllten vorher vier der zwölf Vorschauzeilen – die Übersicht ging an die
 * lauteste Aufgabe. Gezeigt wird deshalb je Aufgabe die **jüngste** Zeile, der Rest steht
 * hinter „+ N weitere" und klappt an Ort und Stelle auf (die Daten sind geladen, kein
 * Rundlauf).
 *
 * Siehe `gruppieren` für die eine Ausnahme – Erwähnungen.
 *
 * ## Abhaken und Ausblenden (21.08.2026, [E-203](../../../../docs/ENTSCHEIDUNGEN.md))
 *
 * Nils: *„Hier wäre es noch cool, wenn man die Gelesenen dann standardmäßig ausblenden
 * könnte."* Jede Zeile trägt dafür ein Häkchen; abgehakt wird **je Aufgabe**, weil auch die
 * Zeile eine Aufgabe ist. Der Stand liegt am Konto, nicht im Browser (`feed_gelesen`) – wer
 * zu Hause abhakt, findet den Stapel im Büro nicht wieder vor.
 *
 * Ausgeblendet wird über einen Haken im Filtermenü, **standardmäßig an**, mit zehn Minuten
 * Nachlauf (`NACHLAUF_MS` in `lib/gelesen.ts`, seit dem 25.08.2026 mit „Klopft an" geteilt).
 * Als Filter und nicht als feste Regel: sonst gäbe es keinen Weg zurück zu dem, was man
 * abgehakt hat.
 */
export function Aktivitaetsfeed() {
  const { data: feed = [] } = useQuery(aktivitaetsfeedQuery(14))
  const [typen, setTypen] = useMerkerMenge<TaskEventType>(
    'feed-filter',
    TASK_EVENT_TYPES,
    FILTER_STANDARD,
  )
  const [ausblenden, setAusblenden] = useMerker('feed-gelesen-aus', 'an', AUSBLENDEN_WERTE)
  const [popupOffen, setPopupOffen] = useState(false)
  const jetzt = useNachlaufTakt(feed)

  const gruppen = gruppieren(filtern(feed, typen, ausblenden === 'an', jetzt))

  /**
   * **Leer heißt leer, gefiltert-leer heißt nicht leer.** Ohne diese Unterscheidung
   * verschwände der ganze Abschnitt, sobald jemand einen Filter setzt, der nichts trifft –
   * mitsamt dem Knopf, mit dem er ihn wieder loswürde.
   */
  if (feed.length === 0) return null

  return (
    <>
      <h2 className="mt-[26px] mb-3 flex flex-wrap items-baseline gap-2 text-sm font-bold tracking-[-0.1px]">
        <button
          type="button"
          onClick={() => setPopupOffen(true)}
          className="cursor-pointer font-bold hover:underline"
          title="Alle Ereignisse ansehen – 14 oder 30 Tage"
        >
          Neu für dich
        </button>
        <small className="text-xs font-normal text-muted">
          Was andere auf deinen Aufgaben getan haben – 14 Tage
        </small>
        <span className="ml-auto font-normal">
          <FeedFilter
            feed={feed}
            typen={typen}
            setTypen={setTypen}
            ausblenden={ausblenden === 'an'}
            setAusblenden={(an) => setAusblenden(an ? 'an' : 'aus')}
            jetzt={jetzt}
          />
        </span>
      </h2>

      <Card className="mb-4">
        <CardPad>
          {gruppen.length === 0 ? (
            <div className="py-1 text-[12.5px] text-ink2">
              {ausblenden === 'an' && feed.some((e) => e.gelesenAm)
                ? 'Nichts Ungelesenes in dieser Auswahl – „Gelesene ausblenden" im Filter zeigt den Rest.'
                : 'Nichts in dieser Auswahl – die anderen Einträge im Filter zeigen, wo etwas liegt.'}
            </div>
          ) : (
            gruppen.slice(0, VORSCHAU_AUFGABEN).map((g) => <FeedGruppe key={g.taskId} gruppe={g} />)
          )}

          {/**
           * **Der Weg zum Rest steht unter der Liste**, nicht nur im Titel. „Neu für dich"
           * anklickbar zu machen ist die Abkürzung für den, der sie kennt; diese Zeile ist
           * für den, der zwölf Zeilen gelesen hat und wissen will, ob es weitergeht.
           *
           * **Und sie steht immer da** (Nils, 18.08.2026: *„wenn ich den Filter nutze, dann
           * steht unten nicht mehr «alle anzeigen»"*). Sie hing vorher daran, dass mehr als
           * zwölf Zeilen übrig sind – ein gesetzter Filter nahm damit genau den Weg weg, auf
           * dem man den größeren Zeitraum erreicht. Was sie sagt, ändert sich; dass es sie
           * gibt, nicht.
           *
           * **„weitere Aufgaben", nicht nur „weitere"** – seit es in den Zeilen ein „+ N
           * weitere" gibt, das Ereignisse zählt, wäre dasselbe Wort für zwei Einheiten hier
           * unten die falsche Zahl im Kopf.
           */}
          <button
            type="button"
            onClick={() => setPopupOffen(true)}
            className="mt-2 cursor-pointer text-[11.5px] text-accent-deep hover:underline"
          >
            {gruppen.length > VORSCHAU_AUFGABEN
              ? `${gruppen.length - VORSCHAU_AUFGABEN} weitere Aufgaben · alle ansehen`
              : 'Alle ansehen · 14 oder 30 Tage'}
          </button>
        </CardPad>
      </Card>

      {popupOffen && (
        <FeedPopup
          typen={typen}
          setTypen={setTypen}
          ausblenden={ausblenden === 'an'}
          setAusblenden={(an) => setAusblenden(an ? 'an' : 'aus')}
          schliessen={() => setPopupOffen(false)}
        />
      )}
    </>
  )
}

/* ── Popup ──────────────────────────────────────────────────────────── */

/**
 * **Dasselbe Fenster, ohne die Vorschau davor**
 * ([Z-12](../../../../docs/phasen/zusatz-12-mein-tag-einfach.md)).
 *
 * Der Modus „Einfach" zeigt Kommentare in der Nachrichtenspalte und alles Übrige nur als Zahl.
 * Hinter dieser Zahl muss der **ganze** Feed liegen – und zwar der, den es schon gibt: mit
 * Filter, Zeitraum und Häkchen.
 *
 * Der Merker-Zustand steckt hier drin und nicht in den Aufrufern, damit beide Wege in dasselbe
 * Fenster dieselbe Filterwahl sehen. Zwei Zustände für dieselbe Liste wären zwei, von denen man
 * immer den falschen im Kopf hat.
 */
export function FeedFenster({ schliessen }: { schliessen: () => void }) {
  const [typen, setTypen] = useMerkerMenge<TaskEventType>(
    'feed-filter',
    TASK_EVENT_TYPES,
    FILTER_STANDARD,
  )
  const [ausblenden, setAusblenden] = useMerker('feed-gelesen-aus', 'an', AUSBLENDEN_WERTE)

  return (
    <FeedPopup
      typen={typen}
      setTypen={setTypen}
      ausblenden={ausblenden === 'an'}
      setAusblenden={(an) => setAusblenden(an ? 'an' : 'aus')}
      schliessen={schliessen}
    />
  )
}

/**
 * **Der ganze Feed, mit Zeitraum** (Nils, 18.08.2026: *„14 Tage, 30 Tage mit anbieten"*).
 *
 * Der gewählte Zeitraum wird gemerkt, der Filter ist derselbe wie in der Vorschau – wer
 * hier „nur Kommentare" einstellt, findet die Vorschau danach ebenso eingestellt vor. Zwei
 * getrennte Filter für dieselbe Liste wären zwei Zustände, von denen man immer den falschen
 * im Kopf hat.
 *
 * Esc schließt, aber erst die Aufgabe darüber, wenn eine offen ist – dieselbe Zusage wie im
 * Tages-Popup.
 */
function FeedPopup({
  typen,
  setTypen,
  ausblenden,
  setAusblenden,
  schliessen,
}: {
  typen: ReadonlySet<TaskEventType>
  setTypen: (wert: ReadonlySet<TaskEventType>) => void
  ausblenden: boolean
  setAusblenden: (an: boolean) => void
  schliessen: () => void
}) {
  const [zeitraum, setZeitraum] = useMerker<Zeitraum>('feed-tage', '14', ZEITRAEUME)
  const detailOffen = useAufgabeOffen()

  /**
   * 400 Zeilen für beide Zeiträume, nicht 80 wie in der Vorschau: bei 30 Tagen wäre die
   * Liste sonst still abgeschnitten, und ein Ende ohne Vermerk liest sich wie ein Ende.
   */
  const { data: feed = [], isLoading } = useQuery(aktivitaetsfeedQuery(Number(zeitraum), 400))
  const jetzt = useNachlaufTakt(feed)
  const gefiltert = filtern(feed, typen, ausblenden, jetzt)
  /** Dieselbe Gruppierung wie in der Vorschau – zwei Darstellungen derselben Liste wären zwei
   * Antworten auf „hab ich das schon gesehen?". Die **Anzahl im Kopf zählt weiter Ereignisse**:
   * sie beantwortet, wie viel der Filter wegnimmt, nicht wie viele Kästen unten stehen. */
  const gruppen = gruppieren(gefiltert)

  useEffect(() => {
    function taste(e: KeyboardEvent) {
      if (e.key !== 'Escape') return
      e.preventDefault()
      if (!detailOffen) schliessen()
    }
    window.addEventListener('keydown', taste)
    return () => window.removeEventListener('keydown', taste)
  }, [detailOffen, schliessen])

  return (
    <div
      className="fixed inset-x-0 bottom-0 unter-leiste z-40 flex items-start justify-center bg-[rgb(23_22_20/0.32)] p-3 pt-[4vh] sm:p-6 sm:pt-[8vh]"
      {...hintergrundSchliesst(schliessen)}
      role="dialog"
      aria-modal="true"
      aria-label="Neu für dich"
    >
      <div className="flex max-h-[min(90vh,100%)] sm:max-h-[min(80vh,100%)] w-full min-w-0 max-w-[820px] flex-col rounded-card border border-line bg-surface shadow-card">
        <div className="flex items-center gap-2 border-b border-line px-[18px] py-3.5">
          <h3 className="text-sm font-bold">Neu für dich</h3>
          <span className="text-xs text-muted">
            {/**
             * **„X von Y" sobald irgendetwas wegfällt** – seit „Gelesene ausblenden" dazukam,
             * reicht `typen.size` als Frage nicht mehr: der Haken nimmt Zeilen weg, ohne dass
             * eine Art gefiltert wäre. Verglichen wird deshalb, was übrig ist.
             */}
            {isLoading
              ? 'lädt …'
              : gefiltert.length === feed.length
                ? `${feed.length} ${feed.length === 1 ? 'Ereignis' : 'Ereignisse'}`
                : `${gefiltert.length} von ${feed.length} Ereignissen`}
          </span>
          <button
            type="button"
            onClick={schliessen}
            className="ml-auto cursor-pointer rounded-lg px-2 py-1 text-ink2 hover:bg-[#f3f2ef]"
            aria-label="Schließen"
          >
            ✕
          </button>
        </div>

        <div className="flex flex-wrap items-center gap-1.5 border-b border-line px-[18px] py-2.5">
          {ZEITRAEUME.map((z) => (
            <button
              key={z}
              type="button"
              onClick={() => setZeitraum(z)}
              className={cn(
                'cursor-pointer rounded-lg px-[11px] py-[5px] text-xs font-semibold',
                zeitraum === z
                  ? 'bg-ink text-white'
                  : 'border border-line2 text-ink2 hover:bg-[#f3f2ef]',
              )}
              aria-pressed={zeitraum === z}
            >
              {z} Tage
            </button>
          ))}
          <span className="ml-auto">
            <FeedFilter
              feed={feed}
              typen={typen}
              setTypen={setTypen}
              ausblenden={ausblenden}
              setAusblenden={setAusblenden}
              jetzt={jetzt}
            />
          </span>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-[18px] py-3.5">
          {isLoading && <div className="py-2 text-[13px] text-ink2">Lädt …</div>}

          {!isLoading && gefiltert.length === 0 && (
            <div className="py-2 text-[13px] text-ink2">
              {feed.length === 0
                ? `In den letzten ${zeitraum} Tagen hat niemand etwas auf deinen Aufgaben getan.`
                : ausblenden && feed.some((e) => e.gelesenAm)
                  ? 'Alles abgehakt – „Gelesene ausblenden" im Filter zeigt es wieder.'
                  : 'Nichts in dieser Auswahl – der Filter zeigt, wo etwas liegt.'}
            </div>
          )}

          {gruppen.map((g) => (
            <FeedGruppe key={g.taskId} gruppe={g} />
          ))}

          <p className="mt-2.5 text-[11px] text-muted">
            Klick auf eine Aufgabe öffnet das Detail · „+ N weitere" zeigt den Rest zu derselben
            Aufgabe · Das Häkchen rechts hakt ab, die Zeile geht zehn Minuten später · Esc schließt
            · Nur fremde Taten – die eigenen stehen nicht drin.
          </p>
        </div>
      </div>
    </div>
  )
}

/* ── Filter ─────────────────────────────────────────────────────────── */

/**
 * **Der Filterknopf mit Auswahlmenü** (Nils, 18.08.2026: *„multiselect"*).
 *
 * ## Warum die Einträge aus den Daten kommen und nicht aus einer Liste
 *
 * Es gibt siebzehn Ereignistypen. Ein Menü, das alle aufzählt, ist zu dreiviertel grau und
 * verlangt trotzdem, gelesen zu werden. Angeboten wird deshalb, **was im geladenen Zeitraum
 * wirklich vorkommt**, häufigstes zuerst und mit seiner Anzahl. Bei Nils sind das genau die
 * drei, nach denen er gefragt hat – Status geändert, Kommentar, Label geändert – plus was
 * sonst gerade anfiel.
 *
 * Gezählt wird über die **ungefilterte** Menge; sonst schrumpfte das Menü mit jedem Haken,
 * und der zweite Filter wäre nicht mehr erreichbar.
 *
 * **Keine Auswahl heißt alles.** Das ist der Unterschied zu „alle Haken gesetzt": kommt
 * morgen ein Typ dazu, den es heute nicht gibt, erscheint er – statt still herauszufallen,
 * weil sein Haken fehlt.
 */
function FeedFilter({
  feed,
  typen,
  setTypen,
  ausblenden,
  setAusblenden,
  jetzt,
}: {
  feed: readonly FeedEintrag[]
  typen: ReadonlySet<TaskEventType>
  setTypen: (wert: ReadonlySet<TaskEventType>) => void
  ausblenden: boolean
  setAusblenden: (an: boolean) => void
  jetzt: number
}) {
  const [offen, setOffen] = useState(false)
  const huelle = useRef<HTMLDivElement>(null)

  /** Klick daneben und Esc schließen – ein Menü ohne beides bleibt im Weg stehen. */
  useEffect(() => {
    if (!offen) return
    function daneben(e: MouseEvent) {
      if (!huelle.current?.contains(e.target as Node)) setOffen(false)
    }
    function taste(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.stopPropagation()
        setOffen(false)
      }
    }
    window.addEventListener('mousedown', daneben)
    window.addEventListener('keydown', taste, true)
    return () => {
      window.removeEventListener('mousedown', daneben)
      window.removeEventListener('keydown', taste, true)
    }
  }, [offen])

  const mengen = new Map<TaskEventType, number>()
  for (const e of feed) {
    const typ = e.typ as TaskEventType
    mengen.set(typ, (mengen.get(typ) ?? 0) + 1)
  }
  /**
   * Häufigstes zuerst; bei Gleichstand nach der **Beschriftung**, nicht nach dem
   * Ereignisschlüssel. Wer das Menü liest, sieht „Kommentar" und „Label geändert" – eine
   * Reihenfolge nach `comment`/`label_changed` wäre nur zufällig dieselbe.
   */
  const angebot = [...mengen.entries()].sort(
    (a, b) =>
      b[1] - a[1] ||
      (TASK_EVENT_TYPE_LABELS[a[0]] ?? a[0]).localeCompare(
        TASK_EVENT_TYPE_LABELS[b[0]] ?? b[0],
        'de',
      ),
  )

  function umschalten(typ: TaskEventType) {
    const neu = new Set(typen)
    if (neu.has(typ)) neu.delete(typ)
    else neu.add(typ)
    setTypen(neu)
  }

  /**
   * **Wie viele Zeilen der Gelesen-Haken gerade wegnimmt.** Die Zahl steht neben dem Eintrag
   * wie bei den Arten – ein Haken, der ohne Zahl dasteht, lässt offen, ob er etwas tut.
   * Gezählt wird über die **ungefilterte** Menge, aus demselben Grund wie oben.
   */
  const gelesenAnzahl = feed.filter((e) => verblasst(e, jetzt)).length

  /**
   * **Der Gelesen-Haken zählt mit** (21.08.2026). Er ist standardmäßig an und nimmt Zeilen
   * weg – bliebe er aus der Zahl heraus, stünde am Knopf „Filter 1", während zwei Filter
   * wirken. Wer eine Zeile vermisst, muss am Knopf sehen können, dass etwas gefiltert ist.
   */
  const aktiv = typen.size + (ausblenden ? 1 : 0)

  return (
    <div className="relative inline-block" ref={huelle}>
      <button
        type="button"
        onClick={() => setOffen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={offen}
        className={cn(
          'cursor-pointer rounded-lg border px-[11px] py-[5px] text-xs font-semibold',
          aktiv > 0
            ? 'border-accent bg-accent-soft text-accent-deep'
            : 'border-line2 text-ink2 hover:bg-[#f3f2ef]',
        )}
      >
        Filter
        {aktiv > 0 && <span className="tabular ml-1.5">{aktiv}</span>}
      </button>

      {offen && (
        <div
          role="menu"
          aria-label="Nach Art filtern"
          className="absolute right-0 z-30 mt-1 max-h-[300px] w-[248px] overflow-y-auto rounded-[9px] border border-line2 bg-surface py-1 text-left shadow-lg"
        >
          {/**
           * **Der Gelesen-Haken steht oben und für sich.** Er filtert nicht nach *Art*,
           * sondern nach *Zustand* – zwischen „Kommentar" und „Label geändert" gestellt,
           * läse er sich wie eine dritte Ereignisart. Die Linie darunter ist die Grenze
           * zwischen beiden Fragen.
           */}
          <button
            type="button"
            role="menuitemcheckbox"
            aria-checked={ausblenden}
            onClick={() => setAusblenden(!ausblenden)}
            className="flex w-full cursor-pointer items-center gap-2 border-b border-line px-2.5 py-1.5 pb-2 text-left text-[12.5px] hover:bg-[#f3f2ef]"
          >
            <span
              className={cn(
                'flex size-[15px] shrink-0 items-center justify-center rounded-[4px] border text-[10px] leading-none text-white',
                ausblenden ? 'border-accent bg-accent' : 'border-line2',
              )}
              aria-hidden
            >
              {ausblenden ? '✓' : ''}
            </span>
            <span className="min-w-0 flex-1 truncate">Gelesene ausblenden</span>
            {gelesenAnzahl > 0 && (
              <span className="tabular shrink-0 text-[11px] text-muted">{gelesenAnzahl}</span>
            )}
          </button>

          {angebot.length === 0 && (
            <div className="px-2.5 py-1.5 text-[12px] text-muted">Nichts zu filtern.</div>
          )}

          {angebot.map(([typ, menge]) => {
            const an = typen.has(typ)
            return (
              <button
                key={typ}
                type="button"
                role="menuitemcheckbox"
                aria-checked={an}
                onClick={() => umschalten(typ)}
                className="flex w-full cursor-pointer items-center gap-2 px-2.5 py-1.5 text-left text-[12.5px] hover:bg-[#f3f2ef]"
              >
                <span
                  className={cn(
                    'flex size-[15px] shrink-0 items-center justify-center rounded-[4px] border text-[10px] leading-none text-white',
                    an ? 'border-accent bg-accent' : 'border-line2',
                  )}
                  aria-hidden
                >
                  {an ? '✓' : ''}
                </span>
                <span className="min-w-0 flex-1 truncate">
                  {TASK_EVENT_TYPE_LABELS[typ] ?? typ}
                </span>
                <span className="tabular shrink-0 text-[11px] text-muted">{menge}</span>
              </button>
            )
          })}

          {/**
           * **„Aufheben" hebt alles auf**, auch das Ausblenden – ein Knopf, der einen von
           * zwei wirkenden Filtern stehen lässt, erklärt die weiterhin fehlenden Zeilen nicht.
           */}
          {aktiv > 0 && (
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setTypen(new Set())
                setAusblenden(false)
              }}
              className="mt-1 w-full cursor-pointer border-t border-line px-2.5 pt-1.5 pb-1 text-left text-[12px] text-accent-deep hover:bg-[#f3f2ef]"
            >
              Filter aufheben
            </button>
          )}
        </div>
      )}
    </div>
  )
}

/** Leere Auswahl heißt „alles" – siehe `FeedFilter`. */
function filtern(
  feed: readonly FeedEintrag[],
  typen: ReadonlySet<TaskEventType>,
  ausblenden: boolean,
  jetzt: number,
) {
  const nachArt = typen.size === 0 ? feed : feed.filter((e) => typen.has(e.typ as TaskEventType))
  return ausblenden ? nachArt.filter((e) => !verblasst(e, jetzt)) : nachArt
}

/**
 * **Abgehakt und der Nachlauf ist um** – erst dann wird ausgeblendet
 * ([E-203](../../../../docs/ENTSCHEIDUNGEN.md)).
 *
 * Gefiltert wird je **Ereignis**, nicht je Aufgabe: `gelesenAm` steht nur an den Zeilen, die
 * beim Abhaken schon da waren (der Server vergleicht gegen `bis`). Kommt danach ein neuer
 * Kommentar, hat er kein `gelesenAm` – die Aufgabe erscheint wieder, und zwar **nur mit dem
 * Neuen** statt mit dem ganzen schon gelesenen Stapel.
 */
function verblasst(e: FeedEintrag, jetzt: number): boolean {
  return !!e.gelesenAm && !imNachlauf(e.gelesenAm, jetzt)
}

/* ── Gruppierung nach Aufgabe ───────────────────────────────────────── */

type Gruppe = {
  taskId: string
  /** Was ohne Zutun sichtbar ist: die jüngste Zeile, ggf. gefolgt von einer Erwähnung. */
  offen: FeedEintrag[]
  /** Was hinter „+ N weitere" liegt. */
  rest: FeedEintrag[]
}

/**
 * **Eine Zeile je Aufgabe** (Nils, 20.08.2026, [E-198](../../../../docs/ENTSCHEIDUNGEN.md)).
 *
 * Der Server liefert absteigend nach Zeit; die Gruppen behalten diese Reihenfolge, weil eine
 * `Map` die Reihenfolge des ersten Auftretens bewahrt. Eine Aufgabe steht damit dort, wo ihr
 * jüngstes Ereignis stünde – die Zeitstempel der Liste bleiben von oben nach unten fallend.
 *
 * ## Die eine Ausnahme: Erwähnungen
 *
 * Eine Erwähnung ist eine Frage an einen persönlich, ein Kommentar nur eine Mitteilung. Wäre
 * die Regel „genau eine Zeile", könnte sie unter der Klappe liegen, sobald jemand danach noch
 * etwas an derselben Aufgabe tut – ausgerechnet die Zeile, die man am wenigsten übersehen
 * will. Deshalb steht sie als **zweite** offene Zeile darunter, wenn sie nicht ohnehin die
 * jüngste ist.
 *
 * Zweite Zeile und nicht erste: der Kopf trägt den Titel, und wenn die Liste nach Zeit fällt,
 * darf die Zeile darunter nicht neuer sein als die darüber. Mehr als eine Erwähnung je Aufgabe
 * ist der seltene Fall, in dem auch sie in den Rest rutscht – dafür sagt der Aufklapper, dass
 * dort eine liegt.
 */
function gruppieren(feed: readonly FeedEintrag[]): Gruppe[] {
  const nachAufgabe = new Map<string, FeedEintrag[]>()
  for (const e of feed) {
    const vorhanden = nachAufgabe.get(e.taskId)
    if (vorhanden) vorhanden.push(e)
    else nachAufgabe.set(e.taskId, [e])
  }

  return [...nachAufgabe].map(([taskId, eintraege]) => {
    const [kopf, ...weitere] = eintraege as [FeedEintrag, ...FeedEintrag[]]
    const erwaehnung = kopf.erwaehntMich ? undefined : weitere.find((e) => e.erwaehntMich)
    return {
      taskId,
      offen: erwaehnung ? [kopf, erwaehnung] : [kopf],
      rest: erwaehnung ? weitere.filter((e) => e !== erwaehnung) : weitere,
    }
  })
}

/* ── Eine Aufgabe ───────────────────────────────────────────────────── */

/**
 * **Der Aufklapper klappt hier auf, nicht woanders hin.** Der ganze Feed ist geladen – ein
 * Klick, der einen Rundlauf auslöste oder das Aufgabendetail öffnete, um drei Kommentare zu
 * zeigen, wäre teurer als die Zeilen, die er spart (Leitplanke 1 und 2).
 *
 * Die aufgeklappten Zeilen tragen **keinen Titel** noch einmal: er steht in der Kopfzeile
 * darüber, und dreimal derselbe Titel untereinander ist genau das, was das Gruppieren
 * abschaffen sollte.
 */
function FeedGruppe({ gruppe }: { gruppe: Gruppe }) {
  const [aufgeklappt, setAufgeklappt] = useState(false)
  const erwaehnungImRest = gruppe.rest.some((e) => e.erwaehntMich)

  const gelesenSetzen = useGelesenSetzen()
  const kopf = gruppe.offen[0]
  /**
   * **Abgehakt wird bis zur jüngsten sichtbaren Zeile.** Das ist der Kopf: der Server liefert
   * absteigend, und die Gruppierung behält die Reihenfolge. Was ein Typfilter gerade
   * ausblendet, bleibt bewusst außen vor – man kann nur abhaken, was man sehen konnte.
   */
  const gelesen = !!kopf?.gelesenAm

  return (
    <div
      className={cn(
        'border-b border-line py-1.5 last:border-b-0',
        /** Im Nachlauf blass: die Zeile ist erledigt, aber noch da – und der Klick zurücknehmbar. */
        gelesen && 'opacity-55',
      )}
    >
      {gruppe.offen.map((e, i) => (
        <FeedZeile
          key={`${String(e.am)}${i}`}
          eintrag={e}
          ohneTitel={i > 0}
          rechts={
            i === 0 && kopf ? (
              <button
                type="button"
                aria-pressed={gelesen}
                aria-label={`${gelesen ? 'Häkchen zurücknehmen' : 'Als gelesen abhaken'}: ${kopf.titel}`}
                title={
                  gelesen
                    ? 'Wieder als ungelesen führen'
                    : 'Als gelesen abhaken – blendet sich in zehn Minuten aus'
                }
                onClick={() =>
                  gelesenSetzen.mutate({
                    taskId: gruppe.taskId,
                    bis: new Date(kopf.am).toISOString(),
                    gelesen: !gelesen,
                  })
                }
                className={cn(
                  'flex size-[17px] shrink-0 cursor-pointer items-center justify-center rounded-[4px] border text-[10px] leading-none',
                  gelesen
                    ? 'border-accent bg-accent text-white'
                    : 'border-line2 text-transparent hover:border-accent hover:text-accent-deep',
                )}
              >
                ✓
              </button>
            ) : undefined
          }
        />
      ))}

      {aufgeklappt &&
        gruppe.rest.map((e, i) => (
          <FeedZeile key={`rest${String(e.am)}${i}`} eintrag={e} ohneTitel />
        ))}

      {gruppe.rest.length > 0 && (
        <button
          type="button"
          onClick={() => setAufgeklappt((a) => !a)}
          aria-expanded={aufgeklappt}
          className="mt-0.5 ml-[82px] cursor-pointer text-[11.5px] text-accent-deep hover:underline"
        >
          {aufgeklappt
            ? 'weniger'
            : `+ ${gruppe.rest.length} ${gruppe.rest.length === 1 ? 'weiteres' : 'weitere'}${
                erwaehnungImRest ? ' · darin eine Erwähnung' : ''
              }`}
        </button>
      )}
    </div>
  )
}

/* ── Eine Zeile ─────────────────────────────────────────────────────── */

function FeedZeile({
  eintrag: e,
  ohneTitel = false,
  rechts,
}: {
  eintrag: FeedEintrag
  ohneTitel?: boolean
  /** Was am rechten Rand steht – das Häkchen der Gruppe, nur an der Kopfzeile. */
  rechts?: ReactNode
}) {
  return (
    <div className={cn('py-0.5', e.erwaehntMich && '-mx-1 rounded-md bg-accent-soft px-1')}>
      <div className="flex flex-wrap items-center gap-2 text-[12.5px]">
        <span className="tabular w-[74px] shrink-0 text-muted">
          {new Date(e.am).toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
          })}{' '}
          {new Date(e.am).toLocaleTimeString('de-DE', {
            hour: '2-digit',
            minute: '2-digit',
          })}
        </span>
        <strong className="shrink-0 text-[12px]">{e.wer ?? 'jemand'}</strong>
        <span className="shrink-0 text-[11.5px] text-ink2">
          {/**
           * **„hat dich erwähnt" statt „Kommentar"** (Nils, 18.08.2026). Ein
           * Kommentar auf einer Aufgabe, die einen ohnehin angeht, ist eine
           * Mitteilung; einer, in dem der eigene Name steht, ist eine Frage. Beide
           * stehen in derselben Liste – aber nicht mit derselben Beschriftung.
           *
           * Im **Filter** bleibt eine Erwähnung trotzdem ein Kommentar: sie ist einer, und
           * ein eigener Eintrag daneben hieße, dass „Kommentare" die Erwähnungen nicht
           * enthält – genau die, die man am wenigsten übersehen will.
           */}
          {e.erwaehntMich ? 'hat dich erwähnt' : (TASK_EVENT_TYPE_LABELS[e.typ] ?? e.typ)}
          {e.nach
            ? ` → ${TASK_STATUS_LABELS[e.nach as keyof typeof TASK_STATUS_LABELS] ?? e.nach}`
            : ''}
        </span>
        {/**
         * **Auch die Folgezeilen führen in die Aufgabe** (Nils, 26.08.2026, vom Telefon:
         * *„kann in Mein Tag unten nicht auf jeden Aufgabentitel klicken … da sehe ich dann
         * nichts"*).
         *
         * Bisher war nur die **erste** Zeile einer Gruppe anklickbar; die weiteren bekamen
         * einen leeren Platzhalter, damit das Herkunftszeichen nicht nach links rutscht. Am
         * Rechner fiel das kaum auf – dort steht der Titel eine Zeile höher in Reichweite. Auf
         * dem Telefon, wo die Gruppe über mehrere Zeilen läuft, tippt man auf die Zeile, die
         * man liest, und nichts passiert.
         *
         * Der Platzhalter ist deshalb jetzt selbst ein Knopf. Er bleibt **leer** – die
         * Wiederholung des Titels war nie das Ziel –, trägt aber `aria-label` und öffnet
         * dieselbe Aufgabe.
         */}
        {ohneTitel ? (
          <AufgabenVerweis
            aufgabeId={e.taskId}
            aria-label={`Aufgabe öffnen: ${e.titel}`}
            title={e.titel}
            className="min-w-0 flex-1 cursor-pointer self-stretch"
          />
        ) : (
          <AufgabenVerweis
            aufgabeId={e.taskId}
            /**
             * **Auf schmalen Schirmen eine eigene Zeile** (Z-10). In der Flex-Zeile stehen
             * Datum, Name, Vorgang und Herkunftszeichen mit fester Breite; für den Titel blieb
             * auf dem Telefon **ein Buchstabe** übrig („Kommentar F"). `basis-full` schiebt
             * ihn unter die Kopfzeile, wo er die ganze Breite hat.
             */
            className="min-w-0 flex-1 basis-full cursor-pointer truncate text-left font-medium hover:underline sm:basis-auto sm:font-normal"
            title={e.titel}
          >
            {e.titel}
          </AufgabenVerweis>
        )}
        {!ohneTitel && <SourceBadge source={e.quelle as never} />}
        {rechts}
      </div>
      {/**
       * **Der Anfang des Kommentars steht dabei.** „Kommentar" allein ist keine
       * Nachricht – man müsste die Aufgabe öffnen, um zu wissen, ob es einen angeht.
       */}
      {/**
       * **Auch hier steht die Unteraufgabe dabei** (22.09.2026,
       * [E-370](../../../../docs/ENTSCHEIDUNGEN.md#e-370)).
       *
       * Dieselbe Zeile wie in „Dich erwähnt", nur im großen Fenster – und derselbe Grund: Wer
       * hier auf den Titel klickt, landet in der Aufgabe und sucht dann. Mit dem Namen der
       * Unteraufgabe weiß er, wonach.
       */}
      {(e.unteraufgabe || e.auszug) && (
        <div className="mt-0.5 ml-[82px] border-l-2 border-accent-soft pl-2 text-[12px] text-ink2">
          {e.unteraufgabe && (
            <p className="truncate text-[11.5px]" title={e.unteraufgabe.titel}>
              <span className="text-muted">↳ Unteraufgabe</span> {e.unteraufgabe.titel}
            </p>
          )}
          {e.auszug && <p>{e.auszug}</p>}
        </div>
      )}
    </div>
  )
}
